import memoizeOne from 'memoize-one';
import { fg } from '@atlassian/jira-feature-gating';
import { metrics } from '@atlassian/browser-metrics';
import { extractProjectKey } from '@atlassian/jira-issue-fetch-services-common/src/common/utils/extract-project-key.tsx';
import {
	buildIssueQuery,
	buildIssueQueryV2,
	ISSUE_VIEW_INTERACTIVE_QUERY,
} from '@atlassian/jira-issue-fetch-services-common/src/services/issue-gira-data/index.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import getMeta from '@atlassian/jira-get-meta';
import { getClient } from '@atlassian/jira-issue-reliability-fetch/src/issue-view-gira-client.tsx';
import type { GraphQlResponse } from '../../types.tsx';

const ISSUE_GIRA_DATA = 'issue-gira-data';
const timeToFetchIssueGiraDataMetric = metrics.custom({
	key: ISSUE_GIRA_DATA,
});

const getCloudId = memoizeOne((): string | null =>
	fg('forge-issue-panel-open-by-default') ? getMeta('ajs-cloud-id') : null,
);

const fetchIssueGiraData = async (issueKey: IssueKey) => {
	const cloudId = getCloudId();
	const projectKey = extractProjectKey(issueKey);
	const variables = {
		issueKey,
		projectKey,
		...(cloudId ? { cloudId } : {}),
	};

	const payload = {
		query: cloudId ? buildIssueQueryV2() : buildIssueQuery(),
		variables,
		operationName: ISSUE_VIEW_INTERACTIVE_QUERY,
	};
	const isInSample = (sampleRate = 100) => Math.random() <= 1 / sampleRate;

	if (isInSample()) {
		timeToFetchIssueGiraDataMetric.start();
	}

	try {
		const response: GraphQlResponse = await getClient(
			'issue-fetch-service/src/services/issue-gira-data/index',
		).query(payload);

		// Best-effort attempt to provide `issueId` and is not guaranteed
		const issueId = response.data?.viewIssue?.issueId;

		timeToFetchIssueGiraDataMetric.stop({
			customData: issueId ? { issueId } : null,
		});

		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		// Cancel the metric and re-throw the error
		timeToFetchIssueGiraDataMetric.cancel();
		throw error;
	}
};

export default fetchIssueGiraData;
